import { Component, OnInit, OnDestroy, forwardRef, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ExtraColumnBbLabelCodeList } from 'app/components/shared/bb-label-code-list/bb-label-code-list.component';
import { PublicationService } from 'app/services/publication.service';
import { Subscription } from 'rxjs';

import { AbstractControl, ControlValueAccessor, NgForm, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { bbDeepCopy } from 'app/shared/utils/bbDeepCopy.util';
import { PublicationCropset } from 'app/interfaces/publication';

interface InternalPublicationCropset extends PublicationCropset {
	disabled?: boolean;
	hidden?: boolean;
}

@Component({
	selector: 'publication-custom-thumbnails',
	templateUrl: './publication-custom-thumbnails.component.html',
	styleUrls: ['./publication-custom-thumbnails.component.scss'],
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => PublicationCustomThumbnailsComponent),
		multi: true
	},
	{
		provide: NG_VALIDATORS,
		multi: true,
		useExisting: forwardRef(() => PublicationCustomThumbnailsComponent),
	}]
})

export class PublicationCustomThumbnailsComponent implements ControlValueAccessor, OnInit, OnDestroy, Validator {
	@Input() form: NgForm; // for form validation

	subscriptions = new Subscription();
	extraFieldsCodeListCropSets: ExtraColumnBbLabelCodeList[];

	standardCropsets: PublicationCropset[];

	modelValue: PublicationCropset[] = null; // received as Input
	internalValue: InternalPublicationCropset[] = null; // for display / the code list

	constructor(
		private translate: TranslateService,
		private publicationService: PublicationService,
	) { }

	// this.publication.customThumbnailSets -> all cropsets available in this publication, including non-active standard and custom added
	// -- All have active true/false
	// -- Standard only have active and name
	// config.cropsets -> all ACTIVE cropsets for this publication. To be used in dropdowns etc.
	// config.standardCropsets -> all standard cropsets available (same accross all publications) with all properties

	ngOnInit() {
		this.extraFieldsCodeListCropSets = [
			{ header: this.translate.instant('WIDTH'), field: 'aspectwidth', type: 'number', required: true },
			{ header: this.translate.instant('HEIGHT'), field: 'aspectheight', type: 'number', required: true },
		];

		this.subscriptions.add(this.publicationService.getConfig().subscribe(config => {
			this.standardCropsets = config.standardCropsets;
			this.writeValue(this.modelValue);
		}));
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	update() {
		// the value to throw to the parent with ngModel
		this.propagateChange(this.parseInternalToModel(this.internalValue));
	}

	// ControlValueAccessor interface methods
	// writeValue: writes the parent value to this compoment
	writeValue(value: any) {
		this.modelValue = value;

		// make sure the standardCropets have loaded, they are needed to set the correct internalValue
		if (!this.standardCropsets) {
			return;
		}
		this.internalValue = this.parseModelToInternal(value);
	}

	// registerOnChange: sends the change to the parent
	registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	propagateChange = (_: any) => { };
	registerOnTouched() { };

	// could not find a way to pass the validate status of bb-label-code-list
	validate(control: AbstractControl): ValidationErrors | null {
		if (!control.value) {
			return null;
		}

		const seen = new Set();

		const duplicateCode = control.value?.some(currentObject => seen.size === seen.add(currentObject.name).size);

		if (duplicateCode) {
			return { duplicate: true };
		}

		return null;
	}


	// saved in publication value -> internal value (UI)
	private parseModelToInternal(value: PublicationCropset[]): InternalPublicationCropset[] {
		if (value) {
			// Make sure it includes all config.standardCropsets, taking the active value from the custom publication
			const cropsetsIncludingStatus: InternalPublicationCropset[] = bbDeepCopy(this.standardCropsets).map((set: InternalPublicationCropset) => {
				set.disabled = true;
				set.active = this.modelValue.some(c => set.name === c.name && c.active) || false;
				set.label = this.translate.instant(set.name.toUpperCase());
				set.hidden = set.name === 'default';
				return set;
			});
			// Add custom added
			this.modelValue.forEach((set) => {
				if (!this.standardCropsets.some(sc => sc.name === set.name)) {
					cropsetsIncludingStatus.push(set);
				}
			});

			return cropsetsIncludingStatus;
		}

		// No publication cropsets explicitly set yet, default to standardCropsets
		return bbDeepCopy(this.standardCropsets).map((set: InternalPublicationCropset) => {
			set.disabled = true;
			set.active = true;
			set.label = this.translate.instant(set.name.toUpperCase());
			set.hidden = set.name === 'default';
			return set;
		});
	}

	// internal value (UI) -> to save in publication value
	private parseInternalToModel(internalValue: InternalPublicationCropset[]): PublicationCropset[] {
		if (!internalValue) {
			return [];
		}

		const modelValue: PublicationCropset[] = bbDeepCopy(internalValue).map((th) => {
			// cleanup for correct save (deleting 'hidden', 'disabled' etc)
			const standardIndex = this.standardCropsets?.findIndex(standard => standard.name === th.name);

			if (standardIndex > -1) {
				// standard cropset only need name + active
				th = { name: th.name, active: th.active };
			} else {
				// custom
				th = {
					name: th.name,
					active: th.active,
					label: th.label,
					// cleanup invalid width/height values
					aspectwidth: th.aspectwidth && th.aspectwidth > 0 ? Math.ceil(th.aspectwidth) : 1,
					aspectheight: th.aspectheight && th.aspectheight > 0 ? Math.ceil(th.aspectheight) : 1,
					aspectratio: Math.round((th.aspectwidth / th.aspectheight) * 10000) / 10000
				};
			}
			return th as PublicationCropset;
		}).filter(th => th.name);

		return modelValue;
	}
}
